import {useCallback, useMemo} from 'react'
import {useWatch} from 'react-hook-form'

import {useFieldConfigurationContext} from '../../configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'
import {useQuantity} from '../quantity/useQuantity'

import {
  computeLoadBalanceRange,
  computeDefaultMaxLoadSize,
  computeInitialSplit,
  computeSplit,
  isLoadBalanceEligible
} from './loadBalance.utils'

/**
 * Provides balance load state for the order form.
 *
 * Balance load splits a delivery into two trucks. The hook reads the current
 * order quantity and truck configuration to determine whether splitting is
 * possible, and exposes the current `maxLoadSize` value together with helpers
 * to update and validate it.
 *
 * The return value is a **discriminated union** on `isEnabled`:
 *
 * - `isEnabled: false` — the current quantity is outside the allowed range
 *   (e.g. single-truck order). `maxLoadSize` is still exposed so the form can
 *   preserve the raw value in the payload without modification.
 *
 * - `isEnabled: true` — splitting is available. All fields are populated and
 *   `setMaxLoadSize` can be called to update the value (it is automatically
 *   clamped to `range.min … range.max`).
 *
 * @example
 * ```tsx
 * const loadBalance = useLoadBalance()
 *
 * if (!loadBalance.isEnabled) {
 *   return null
 * }
 *
 * const {maxLoadSize, setMaxLoadSize, split, range} = loadBalance
 *
 * return (
 *   <LoadBalanceSection
 *     value={maxLoadSize ?? range.defaultValue}
 *     min={range.min}
 *     max={range.max}
 *     step={range.step}
 *     uom={range.uom}
 *     firstTruck={split.firstTruck}
 *     secondTruck={split.secondTruck}
 *     onChange={setMaxLoadSize}
 *   />
 * )
 * ```
 */
export const useLoadBalance = () => {
  // balance load form control
  const {
    control,
    setValue,
    formState: {defaultValues}
  } = useOrderProcessFormContext()
  const maxLoadSize = useWatch({name: 'maxLoadSize', control})
  const config = useFieldConfigurationContext('loadBalance')

  // dependencies
  const quantityReturn = useQuantity()
  const {quantityConfiguration, defaultQuantity} = quantityReturn
  const volume = quantityReturn.type === 'volume' ? quantityReturn.volume : 0
  const {step, uom} = quantityConfiguration
  const truckSize = config.isEnabled ? config.maxTruckSize : 0

  const loadBalanceRange = useMemo(() => {
    if (!isLoadBalanceEligible(config, volume)) return undefined
    return computeLoadBalanceRange({
      quantity: volume,
      truckSize,
      quantityStep: step
    })
  }, [config, volume, truckSize, step])

  /**
   * The split from the original order values — memoized once from stable default values.
   * undefined when balance load was not active for the original order.
   */
  const defaultSplit = useMemo(
    () =>
      defaultQuantity === undefined
        ? undefined
        : computeInitialSplit({
            config,
            defaultQuantity,
            truckSize,
            step,

            defaultMaxLoadSize: defaultValues?.maxLoadSize
          }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const setMaxLoadSize = useCallback(
    (value: number) => {
      if (!loadBalanceRange) return
      const clampedMaxLoadSize = Math.min(
        Math.max(value, loadBalanceRange.min),
        loadBalanceRange.max
      )
      setValue('maxLoadSize', clampedMaxLoadSize, {shouldValidate: true})
    },
    [loadBalanceRange, setValue]
  )

  /**
   * Resets maxLoadSize back to the original order value (defaultValues.maxLoadSize).
   * Called by the adjustment hook when leaving the two-truck quantity range so the
   * payload reflects the original truck capacity rather than a stale split value.
   */
  const resetToDefault = useCallback(() => {
    setValue('maxLoadSize', defaultValues?.maxLoadSize, {
      shouldValidate: true
    })
  }, [setValue, defaultValues])

  return useMemo(() => {
    if (!loadBalanceRange) {
      return {isEnabled: false, maxLoadSize, resetToDefault, defaultSplit} as const
    }

    const rangeDefaultValue = computeDefaultMaxLoadSize(volume, step)

    return {
      isEnabled: true,
      maxLoadSize,
      setMaxLoadSize,
      resetToDefault,
      split: computeSplit(maxLoadSize ?? rangeDefaultValue, volume, step),
      range: {...loadBalanceRange, step, uom, defaultValue: rangeDefaultValue},
      defaultSplit
    } as const
  }, [
    volume,
    loadBalanceRange,
    maxLoadSize,
    step,
    uom,
    setMaxLoadSize,
    resetToDefault,
    defaultSplit
  ])
}
